import { Drawer, Group } from "@mantine/core"
import { zodResolver } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import { IconPlus } from "@tabler/icons-react"
import {
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table"
import { MaterialTypeCategory } from "@tfg/db"
import { Button } from "@tfg/ui"
import { useState } from "react"
import { z } from "zod"

import { AdminResponses } from "@tfg/types/endpoints/admin"
import { MaterialTypeCategorySchema } from "@tfg/types/schemas/enum"
import { GetEcobalyseMaterialTypesResponseDto } from "@tfg/types/schemas/modules/lifeCycleAnalysis/dtos/get-ecobalyse-material-types.response.dto"

import { Table } from "~/components/table/Table"
import { useCreateMaterialTypeMutation } from "~/pages/Admin/MasterLists/MaterialTypes/hooks/useCreateMaterialTypeMutation"

import { FormFieldConfig, TypesForm } from "../../components/TypesForm"

import { useColumns } from "./columns"

type Props = {
  materialTypes: AdminResponses.MaterialTypes[]
  materialOpChains: AdminResponses.OperationChainElement[]
  ecobalyseMaterialTypes: GetEcobalyseMaterialTypesResponseDto
}

export function MaterialTypesAdmin({
  materialTypes,
  materialOpChains,
  ecobalyseMaterialTypes,
}: Props) {
  const columns = useColumns(materialOpChains, ecobalyseMaterialTypes)
  const [sorting, setSorting] = useState<SortingState>([
    { id: "key", desc: false },
  ])
  const [drawerOpen, drawerActions] = useDisclosure(false)
  const table = useReactTable({
    data: materialTypes,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const { mutate: createMaterialType } = useCreateMaterialTypeMutation({
    onSuccess: drawerActions.close,
  })

  const reactFormConfig = {
    validate: zodResolver(
      z.object({
        key: z.string().min(1),
        category: MaterialTypeCategorySchema,
        ecobalyseId: z.string().nullable(),
        operationChainId: z.string().uuid(),
      })
    ),
    initialValues: {
      key: "",
      category: MaterialTypeCategory.SYNTHETIC,
      ecobalyseId: "",
      operationChainId: materialOpChains[0].id,
    },
  }
  const fields: FormFieldConfig[] = [
    {
      type: "text",
      name: "key",
      label: "Key",
      placeholder: "Material Type Key",
    },
    {
      type: "select",
      name: "category",
      label: "Category",
      placeholder: "Material Type Category",
      options: Object.values(MaterialTypeCategory).map((value) => ({
        label: value,
        value,
      })),
    },
    {
      type: "text",
      name: "ecobalyseId",
      label: "Ecobalyse Id",
      placeholder: "Ecobalyse Id",
    },
    {
      type: "select",
      name: "operationChainId",
      label: "Operation Chain",
      placeholder: "Material Operation Chain",
      options: materialOpChains.map((opChain) => ({
        label: opChain.name,
        value: opChain.id,
      })),
    },
  ]
  return (
    <>
      <Group justify="right">
        <Button onClick={drawerActions.open} icon={<IconPlus />}>
          New Material Type
        </Button>
      </Group>
      <Drawer
        position="right"
        opened={drawerOpen}
        onClose={() => drawerActions.close()}
        title="Create Material Type"
        size="lg"
      >
        <TypesForm
          fields={fields}
          submitLabel="Create"
          formConfig={reactFormConfig}
          mutate={createMaterialType}
        />
      </Drawer>
      <Table table={table} pagination={false} />
    </>
  )
}
